import type { SessionUser } from '@afiliados/shared';
import { useAuthStore } from '@/features/auth/store';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

const BASE = import.meta.env.VITE_API_URL ?? '';

let refreshing: Promise<string | null> | null = null;

async function refreshToken(): Promise<string | null> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${BASE}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { accessToken: string; user: SessionUser };
      useAuthStore.getState().setSession(data.accessToken, data.user);
      return data.accessToken;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  raw?: boolean;
}

export async function api<T>(path: string, opts: RequestOptions = {}, retry = true): Promise<T> {
  const { body, query, raw, headers, ...rest } = opts;
  const url = new URL(`${BASE}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    }
  }
  const token = useAuthStore.getState().accessToken;
  const isForm = body instanceof FormData;
  const res = await fetch(url, {
    ...rest,
    credentials: 'include',
    headers: {
      ...(isForm ? {} : body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(headers as Record<string, string>),
    },
    body: isForm ? (body as FormData) : body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && retry && !path.startsWith('/api/auth/login')) {
    const newToken = await refreshToken();
    if (newToken) return api<T>(path, opts, false);
    useAuthStore.getState().logout();
  }

  if (!res.ok) {
    let payload: { error?: string; message?: string; details?: unknown } = {};
    try {
      payload = await res.json();
    } catch {
      /* vazio */
    }
    // Senha redefinida por um admin, por exemplo: a tela de troca passa a ser obrigatória.
    if (res.status === 403 && payload.error === 'PASSWORD_CHANGE_REQUIRED') {
      useAuthStore.getState().requirePasswordChange();
    }
    throw new ApiError(
      res.status,
      payload.error ?? 'ERROR',
      payload.message ?? res.statusText,
      payload.details,
    );
  }
  if (res.status === 204) return undefined as T;
  if (raw) return res as unknown as T;
  return (await res.json()) as T;
}
